// Experiment detail (brief §37): header, today, progress, chart, AI insight, notes,
// plan, weekly reviews.
//
// Ordered by what the user came to find out — how is it going — rather than by what
// is easiest to render. The plan sits at the bottom because it changes rarely.

import { useCallback, useEffect, useState } from 'react';
import { api, type ExperimentDetail as Detail, type WeeklyReviewResult } from '../lib/api.ts';
import { MetricChart, MetricTable } from '../components/MetricChart.tsx';
import {
  Button, Card, Delta, DemoBadge, ErrorNote, ProgressBar, SectionTitle, Spinner, TextArea,
} from '../components/ui.tsx';
import { CONFOUNDER_LABELS, longDate, titleCase } from '../lib/format.ts';

export function ExperimentDetail({ id, navigate }: { id: string; navigate: (path: string) => void }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metricIndex, setMetricIndex] = useState(0);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await api.experiments.detail(id));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  if (error && !detail) return <ErrorNote message={error} />;
  if (!detail) return <Spinner />;

  const { experiment, progress, adherence } = detail;
  const chartable = detail.metrics.filter((m) => m.metric.type === 'scale' || m.metric.type === 'number');
  const selected = chartable[Math.min(metricIndex, chartable.length - 1)];
  const isDemo = experiment.name.startsWith('[demo]');
  const finished = experiment.status === 'completed' || experiment.status === 'abandoned';

  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-start gap-2">
          <h1 className="text-xl font-semibold leading-snug">{experiment.name.replace(/^\[demo\]\s*/, '')}</h1>
          {isDemo && <DemoBadge />}
        </div>
        <p className="text-sm text-muted mt-1">
          {titleCase(experiment.status)}
          {progress.day_number !== null && ` · Day ${progress.day_number}`}
          {progress.total_days !== null && ` of ${progress.total_days}`}
          {progress.phase !== 'post' && ` · ${titleCase(progress.phase)}`}
        </p>
        {experiment.goal && <p className="text-sm mt-2">{experiment.goal}</p>}
        {progress.progress !== null && <div className="mt-3"><ProgressBar value={progress.progress} label="Experiment progress" /></div>}
      </header>

      {!finished && (
        <div className="flex gap-2">
          <Button variant="primary" className="flex-1" onClick={() => navigate(`/experiments/${id}/checkin`)}>
            Check in
          </Button>
          <Button onClick={() => navigate(`/experiments/${id}/doctor`)}>For my doctor</Button>
        </div>
      )}

      <AiQuestion experimentId={id} />

      <section>
        <SectionTitle>Progress</SectionTitle>
        <Card className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-ink-2">Adherence</span>
            <span className="text-sm tabular-nums">
              {adherence.percent === null
                ? 'Nothing scheduled yet'
                : `${adherence.completed} / ${adherence.expected} = ${adherence.percent}%`}
            </span>
          </div>
          {(adherence.missed > 0 || adherence.skipped > 0) && (
            <p className="text-xs text-muted">
              {adherence.missed} missed · {adherence.skipped} deliberately skipped
            </p>
          )}
          {detail.per_intervention.length > 1 && (
            <ul className="text-xs text-ink-2 space-y-1 pt-1 border-t border-hairline">
              {detail.per_intervention.map((p) => (
                <li key={p.intervention_id} className="flex justify-between gap-3">
                  <span>{p.name}</span>
                  <span className="tabular-nums">{p.adherence.percent ?? '—'}%</span>
                </li>
              ))}
            </ul>
          )}
          {detail.missing_days.length > 0 && (
            <p className="text-xs text-serious">
              {detail.missing_days.length} day{detail.missing_days.length === 1 ? '' : 's'} with no readings.
            </p>
          )}
        </Card>
      </section>

      {chartable.length > 0 && selected && (
        <section>
          <SectionTitle
            action={
              <button className="text-xs text-accent min-h-11" onClick={() => setShowTable(!showTable)}>
                {showTable ? 'Chart' : 'Table'}
              </button>
            }
          >
            {selected.metric.name}
          </SectionTitle>

          {chartable.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2 mb-1 -mx-1 px-1">
              {chartable.map((m, i) => (
                <button
                  key={m.metric.id}
                  onClick={() => setMetricIndex(i)}
                  aria-pressed={i === metricIndex}
                  className={`shrink-0 min-h-11 px-3 rounded-full border text-xs
                    ${i === metricIndex ? 'bg-accent-soft border-accent' : 'bg-surface border-hairline text-ink-2'}`}
                >
                  {m.metric.name}
                </button>
              ))}
            </div>
          )}

          <Card>
            {showTable ? (
              <MetricTable analysis={selected} />
            ) : (
              <MetricChart
                analysis={selected}
                baselineStart={experiment.baseline_start}
                baselineEnd={experiment.baseline_end}
                interventionStart={experiment.intervention_start}
                end={detail.today}
                confounders={detail.confounders}
              />
            )}

            <div className="mt-3 pt-3 border-t border-hairline space-y-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-ink-2">Baseline average</span>
                <span className="text-sm tabular-nums">{selected.comparison.baseline_average ?? '—'}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-ink-2">Since treatment started</span>
                <span className="text-sm tabular-nums">{selected.comparison.current_average ?? '—'}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3 pt-1">
                <span className="text-sm text-ink-2">Change</span>
                <Delta percent={selected.comparison.percentage_change} direction={selected.comparison.direction} />
              </div>
              {!selected.comparison.has_enough_data && (
                <p className="text-xs text-muted pt-1">
                  Too few readings so far for this comparison to mean much.
                </p>
              )}
            </div>
          </Card>
        </section>
      )}

      <WeeklyReviews experimentId={id} latestWeek={detail.latest_complete_week} />

      {detail.side_effects.length > 0 && (
        <section>
          <SectionTitle>Side effects recorded</SectionTitle>
          <Card>
            <ul className="text-sm space-y-1">
              {[...new Set(detail.side_effects.map((s) => s.name))].map((name) => (
                <li key={name} className="flex justify-between gap-3">
                  <span>{name}</span>
                  <span className="text-xs text-muted">
                    {detail.side_effects.filter((s) => s.name === name).length} day(s)
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {detail.confounders.length > 0 && (
        <section>
          <SectionTitle>Things that may have affected results</SectionTitle>
          <Card>
            <ul className="text-sm space-y-1">
              {[...new Set(detail.confounders.map((c) => c.type))].map((type) => (
                <li key={type} className="flex justify-between gap-3">
                  <span>{CONFOUNDER_LABELS[type] ?? titleCase(type)}</span>
                  <span className="text-xs text-muted">
                    {detail.confounders.filter((c) => c.type === type).length} day(s)
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      <section>
        <SectionTitle>The plan</SectionTitle>
        <Card className="space-y-3">
          {experiment.hypothesis && (
            <div>
              <p className="text-xs text-muted">Hypothesis</p>
              <p className="text-sm mt-0.5">{experiment.hypothesis}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted">Baseline</p>
              <p className="mt-0.5">
                {experiment.baseline_start ? `${longDate(experiment.baseline_start)} – ${longDate(experiment.baseline_end ?? '')}` : 'None'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted">Treatment</p>
              <p className="mt-0.5">
                {experiment.intervention_start
                  ? `${longDate(experiment.intervention_start)} – ${experiment.intervention_end ? longDate(experiment.intervention_end) : 'ongoing'}`
                  : '—'}
              </p>
            </div>
          </div>

          <ul className="space-y-2 pt-1 border-t border-hairline">
            {detail.interventions.map((iv) => (
              <li key={iv.id}>
                <p className="text-sm font-medium">{iv.name}</p>
                {iv.dose && <p className="text-xs text-ink-2">{iv.dose}</p>}
                {/* whitespace-pre-line so the numbered technique steps keep their lines */}
                {iv.instructions && <p className="text-xs text-muted mt-0.5 whitespace-pre-line">{iv.instructions}</p>}
              </li>
            ))}
          </ul>
        </Card>
      </section>

      <ExperimentActions detail={detail} onChange={load} navigate={navigate} />
    </div>
  );
}

/** The one AI question for today, with somewhere to answer it. */
function AiQuestion({ experimentId }: { experimentId: string }) {
  const [state, setState] = useState<{ question: string; reason: string } | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'off' | 'error'>('idle');
  const [answer, setAnswer] = useState('');
  const [answered, setAnswered] = useState(false);
  const [date, setDate] = useState('');

  useEffect(() => {
    void (async () => {
      const ai = await api.ai.status().catch(() => null);
      if (!ai?.enabled) { setStatus('off'); return; }
      setStatus('loading');
      try {
        const res = await api.ai.question(experimentId);
        setState(res.question);
        setDate(new Date().toISOString().slice(0, 10));
        setStatus('idle');
      } catch {
        setStatus('error');
      }
    })();
  }, [experimentId]);

  if (status === 'off' || status === 'error' || !state) {
    return status === 'loading' ? <Spinner label="Thinking of a question" /> : null;
  }

  return (
    <section>
      <SectionTitle>A question for you</SectionTitle>
      <Card className="space-y-3">
        <p className="text-sm">{state.question}</p>
        {answered ? (
          <p className="text-xs text-good">✓ Saved</p>
        ) : (
          <>
            <TextArea value={answer} onChange={setAnswer} rows={2} placeholder="Your answer (optional)" />
            <Button
              disabled={!answer.trim()}
              onClick={() => {
                void api.ai.answerQuestion(experimentId, date, answer.trim()).then(() => setAnswered(true));
              }}
            >
              Save answer
            </Button>
          </>
        )}
      </Card>
    </section>
  );
}

function WeeklyReviews({ experimentId, latestWeek }: { experimentId: string; latestWeek: number | null }) {
  const [review, setReview] = useState<WeeklyReviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (latestWeek === null) return null;

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setReview(await api.ai.weekly(experimentId, latestWeek));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <SectionTitle>Weekly review</SectionTitle>
      <Card className="space-y-3">
        {!review && !loading && (
          <>
            <p className="text-sm text-ink-2">Week {latestWeek} is complete.</p>
            <Button onClick={() => void load()}>Show week {latestWeek}</Button>
          </>
        )}
        {loading && <Spinner label="Working out the numbers" />}
        {error && <ErrorNote message={error} />}

        {review && (
          <>
            <p className="text-xs text-muted">
              Week {review.week} · {review.data.logged_days} of {review.data.expected_days} days logged
            </p>

            <ul className="space-y-1.5">
              {review.data.metrics.filter((m) => m.week_average !== null).map((m) => (
                <li key={m.metric_id} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-ink-2">{m.name}</span>
                  <span className="flex items-baseline gap-2">
                    <span className="tabular-nums">{m.week_average}</span>
                    <Delta percent={m.change_percent} direction={m.direction} />
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-sm pt-2 border-t border-hairline">
              Adherence this week: {review.data.adherence.percent ?? '—'}%
            </p>

            {/* The computed figures render whether or not the AI is available; the
                narration is an addition to them, never a replacement. */}
            {review.review ? (
              <>
                <p className="text-sm leading-relaxed pt-2 border-t border-hairline">{review.review.summary}</p>
                {review.review.data_quality_notes.length > 0 && (
                  <ul className="text-xs text-serious space-y-1">
                    {review.review.data_quality_notes.map((note, i) => <li key={i}>· {note}</li>)}
                  </ul>
                )}
                {review.review.recommended_question && (
                  <p className="text-sm text-ink-2 italic">{review.review.recommended_question}</p>
                )}
              </>
            ) : (
              <p className="text-xs text-muted pt-2 border-t border-hairline">
                AI summaries are off, so this is the raw week.
              </p>
            )}
          </>
        )}
      </Card>
    </section>
  );
}

function ExperimentActions({ detail, onChange, navigate }: {
  detail: Detail;
  onChange: () => void;
  navigate: (path: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const status = detail.experiment.status;
  const finished = status === 'completed' || status === 'abandoned';

  const setStatus = async (next: string): Promise<void> => {
    setBusy(true);
    try {
      await api.experiments.setStatus(detail.experiment.id, next);
      onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pt-2">
      <div className="flex flex-wrap gap-2">
        {finished && (
          <Button variant="primary" onClick={() => navigate(`/experiments/${detail.experiment.id}/report`)}>
            Final report
          </Button>
        )}
        {status === 'paused' && <Button disabled={busy} onClick={() => void setStatus('active')}>Resume</Button>}
        {(status === 'active' || status === 'baseline') && (
          <>
            <Button disabled={busy} onClick={() => void setStatus('paused')}>Pause</Button>
            <Button disabled={busy} onClick={() => void setStatus('completed')}>Finish</Button>
          </>
        )}
        <Button onClick={() => navigate(`/experiments/${detail.experiment.id}/doctor`)}>Doctor summary</Button>
        <a className="tap min-h-11 px-4 rounded-xl border border-line text-sm font-medium inline-flex items-center"
          href={api.settings.csvUrl(detail.experiment.id)} download>
          Export CSV
        </a>
      </div>
    </section>
  );
}
